﻿/**
 * FragmentLifecycleManager
 * Owns handshake, update, and status-transition behavior.
 */

import type { FragmentName, FragmentStatus, FragmentUpdateMessagePayload } from '@anddone/ajs-util';
import type { Events } from './../Events';
import type { Orchestrator } from './../Orchestrator';
import type { StateManagement } from './../StateManagement';
import type { FragmentRegistry } from './../fragment-registry/FragmentRegistry';
import { createLogger } from '@anddone/ajs-logger';
import {
  generateFragmentLifecycleContext,
  generateFragmentUpdateContext,
  isValidStatus,
} from '@anddone/ajs-util';

const logger = createLogger('SDK', 'FragmentLifecycleManager');

export class FragmentLifecycleManager {
  /**
   * constructor
   *
   * @param dataState - Shared config and SDK state
   * @param orchestrator - Reference to orchestrator module
   * @param events - Reference to events module
   * @param fragmentRegistry - Reference to fragmentRegistry module
   */
  constructor(
    private readonly dataState: StateManagement,
    private readonly orchestrator: Orchestrator,
    private readonly events: Events,
    private readonly fragmentRegistry: FragmentRegistry,
  ) {}

  /**
   * Update a fragment iFrame's height.
   *
   * @param fragmentName - Name of the fragment
   * @param height - New iframe height in pixels
   * @returns void
   */
  public setFragmentHeight(fragmentName: FragmentName, height: number): void {
    const fragment = this.fragmentRegistry.get(fragmentName);

    if (!fragment) {
      logger.warn('height-update', `Fragment not found: "${fragmentName}"`, {
        fragmentName,
        height,
      });
      return;
    }

    const normalizedHeight = Math.ceil(Number(height));

    if (!Number.isFinite(normalizedHeight) || normalizedHeight <= 0) {
      logger.warn('height-update', `Invalid height received from "${fragmentName}"`, {
        fragmentName,
        height,
      });
      return;
    }

    if (fragment.height === normalizedHeight) {
      return;
    }

    const previousHeight = fragment.height;

    fragment.height = normalizedHeight;
    fragment.iframe.style.height = `${normalizedHeight}px`;

    logger.debug(
      'height-update',
      `"${fragmentName}" height ${previousHeight}px → ${normalizedHeight}px`,
      {
        fragmentName,
        previousHeight,
        height: normalizedHeight,
      },
    );
  }

  /**
   * Update a fragment's status
   *
   * @param fragmentName - Name of the fragment
   * @param status - Fragment lifecycle status
   * @returns void
   */
  public setFragmentStatus(
    fragmentName: FragmentName,
    status: FragmentStatus,
    error?: string,
  ): void {
    const fragment = this.fragmentRegistry.get(fragmentName);

    if (!fragment) {
      return;
    }

    const previousStatus = fragment.status;
    fragment.status = status;

    logger.debug('status-update', `"${fragmentName}" ${previousStatus} → ${status}`);

    const fragmentLifecycleContext = generateFragmentLifecycleContext(fragment);

    if (status === 'staged' && previousStatus !== 'staged') {
      this.events.emit('fragment-staged', fragmentLifecycleContext);

      if (this.allFragmentsStaged()) {
        logger.info('status-update', 'All fragments staged');
        this.events.emit('all-staged', null);
      }
    }

    if (status === 'submitted' && previousStatus !== 'submitted') {
      if (this.allFragmentsSubmitted()) {
        this.dataState.attempts.endProcessing();
        logger.info('status-update', 'All fragments submitted');
      }
    }

    if (status === 'error') {
      this.dataState.attempts.endProcessing();
      fragment.error = error;
      logger.error('status-update', `"${fragmentName}" entered error state`, error);
      this.events.emit('fragment-error', fragmentLifecycleContext);
    } else {
      // any non-error status resets the error to undefined.
      fragment.error = undefined;
    }

    this.orchestrator.sendStatusUpdateCommandToOrchestratorFragment();
  }

  /**
   * Handle handshake request from a fragment
   *
   * @param fragmentName - Name of the fragment
   * @param fragmentId - ID of the fragment
   * @returns void
   */
  public handleHandshakeRequest(fragmentName: FragmentName, fragmentId: string): void {
    logger.debug('handshake', `Request received from "${fragmentName}"`, { fragmentId });

    const fragment =
      fragmentName === 'orchestrator'
        ? this.fragmentRegistry.getOrchestrator()
        : this.fragmentRegistry.get(fragmentName);

    if (fragment) {
      const fragmentLifecycleContext = generateFragmentLifecycleContext(fragment);

      const previousStatus = fragment.status;
      fragment.status = 'pending';

      logger.debug('handshake', `Fragment "${fragmentName}" marked pending after handshake`, {
        fragmentName,
        fragmentId,
        id: fragment.id,
        previousStatus,
        status: fragment.status,
      });

      // Send handshake response back to the fragment.
      // commandToken is a placeholder here; OrchestratorCommandManager.createCommandMessage
      // stamps the real per-session token before postMessage is called.
      this.orchestrator.sendCommandToFragment(fragment, 'handshake-response', {
        fragmentId: fragment.id,
        sessionId: this.dataState.session.id,
        paymentMethod: this.dataState.paymentMethod.current,
        commandToken: '',
      });

      this.events.emit('fragment-pending', fragmentLifecycleContext);
      this.orchestrator.sendLogCommandToOrchestratorFragment(
        `Handshake completed with fragment: ${fragmentName}`,
      );
      this.orchestrator.sendStatusUpdateCommandToOrchestratorFragment();

      // Now that we've completed the handshake, we know the fragment is loaded. Flush any queued commands.
      this.orchestrator.flushPendingCommands(fragment);

      return;
    }

    logger.warn('handshake', `Fragment "${fragmentName}" not in registry — handshake dropped`, {
      fragmentId,
    });
  }

  /**
   * Handle update from a fragment. Fragments will send status updates, and value data if it is non-sensitive.
   *
   * @param fragmentName - Name of the fragment
   * @param data - Update data from the fragment
   * @returns void
   */
  public handleFragmentUpdate(
    fragmentName: FragmentName,
    data?: FragmentUpdateMessagePayload,
  ): void {
    logger.info('update', `Received from "${fragmentName}"`, data);

    const fragment = this.fragmentRegistry.get(fragmentName);

    if (!fragment) {
      logger.warn('update', `Fragment not found: "${fragmentName}"`);
      return;
    }

    if (data?.status) {
      this.setFragmentStatus(fragmentName, data.status, data?.error);
    }

    if (typeof data?.height === 'number') {
      this.setFragmentHeight(fragmentName, data.height);
    }

    if (data?.values) {
      fragment.values = data.values;
    }

    const fragmentStatusUpdatePayload = generateFragmentUpdateContext(fragment);
    this.events.emit('fragment-update', fragmentStatusUpdatePayload);
    this.orchestrator.sendLogCommandToOrchestratorFragment(
      `Update received from fragment: ${fragmentName} - ${JSON.stringify(data)}`,
    );
  }

  // -------------  Fragment state getters --------------------

  /**
   * Check if a fragment is staged.
   *
   * A submitted fragment is treated as staged-equivalent because it has already
   * passed the staged state.
   *
   * @param fragmentName - Name of the fragment
   * @returns boolean
   */
  public isFragmentStaged(fragmentName: FragmentName): boolean {
    const fragment = this.fragmentRegistry.get(fragmentName);
    return fragment ? isValidStatus(fragment.status) : false;
  }

  /**
   * Check if all processable fragments are staged and ready to be submitted.
   *
   * Submitted counts as staged-equivalent here so a fragment does not become
   * "unstaged" after successfully submitting.
   *
   * @return boolean
   */
  public allFragmentsStaged(): boolean {
    const processableFragments = this.fragmentRegistry.getProcessableFragments();

    if (processableFragments.length === 0) {
      return false;
    }

    return processableFragments.every(([, fragment]) => isValidStatus(fragment.status));
  }

  /**
   * Get all fragments that are not staged
   *
   * @returns string[] - Array of fragment names that are not staged
   */
  public getUnstaggedFragments(): string[] {
    return this.fragmentRegistry
      .getProcessableFragments()
      .filter(([, fragment]) => !isValidStatus(fragment.status))
      .map(([name]) => name);
  }

  /**
   * Check if all processable fragments have been submitted
   *
   * @return boolean
   */
  public allFragmentsSubmitted(): boolean {
    const processableFragments = this.fragmentRegistry.getProcessableFragments();

    if (processableFragments.length === 0) {
      return false;
    }

    return processableFragments.every(([, fragment]) => fragment.status === 'submitted');
  }
}
